import { AuditTable } from './AuditTable'
/**
 * Deal Audit — the single record of how this deal got to where it is.
 *
 * Activity and Audit used to be two pages answering one question badly. The
 * Activity page carried the good artefact — a linked chain of every recorded
 * event, filterable to one stage occupancy — while the Audit page dumped three
 * unexplained lists side by side: the first sixty lineage rows with no
 * indication that there were more, forty raw `event_type` strings, and the
 * notes. A reader could not tell what the page covered, what it left out, or
 * where any single value came from.
 *
 * This is the survivor. One page, three views over the same record, and a
 * coverage header that states plainly what the audit does and does not hold:
 *
 *   Trail       every event since entry, chained and stage-segmented
 *   All fields  every field, its value, the page that owns it, and — per row —
 *               where that value came from and every change since
 *   Notes       what people wrote on the deal
 *
 * Every count is of real records. Where the platform does not capture a source
 * it says so rather than letting the trail read as complete.
 */

import { useMemo, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import { Loader2 } from 'lucide-react'

import type {
  LoanActivityEvent,
  LoanFieldLineageField,
} from '@/pages/pipeline-intelligence/types'
import { loansService, type StageAuditEntry } from '@/services/loans'
import { LOAN_QUERY_KEYS } from '@/services/loanQueryKeys'
import { stageAuditEntries } from './stageAuditEntries'
import { EmptyLine } from './ui'
import { formatFieldValue } from './format'

type AuditView = 'trail' | 'fields' | 'notes'

type Note = { id: string; body: string; author: string; createdAt: string }

const VIEWS: { id: AuditView; label: string; hint: string }[] = [
  { id: 'trail', label: 'Trail', hint: 'Every event since the package arrived, newest first' },
  // Field lineage and All fields listed the same fields twice, from two
  // angles, and a reader comparing them had to hold both lists in their head.
  // One list now: every field, its value, the page that owns it, and — on each
  // row — where the value came from and how it changed.
  { id: 'fields', label: 'All fields', hint: 'Every field, its value, its section and its provenance' },
  { id: 'notes', label: 'Notes', hint: 'What people wrote on this deal' },
]

/** The Trail's columns, named for what they hold (DJA-13). */
const TRAIL_COLUMNS = ['Event', 'Change', 'By', 'When', 'Source'] as const

function stamp(iso: string | null | undefined): string {
  if (!iso) return '—'
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? String(iso) : date.toLocaleString()
}

/**
 * A value as the catalogue writes it. The Trail printed the stored number
 * raw — 'debt yield: 8.875 → 9.793103448275863', 'price per unit: — →
 * 177830.18867924527' (nd-031, nd-071) — so a figure is formatted by its
 * field's kind (money, percent, count), as every other surface shows it. A
 * number whose field kind is unknown is still grouped and cut to four places.
 */
function show(value: unknown, field?: string, kinds?: ReadonlyMap<string, string>): string {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'object') return JSON.stringify(value)
  if (field && typeof value === 'number') {
    return formatFieldValue(value, kinds?.get(field) ?? 'number', field) || '—'
  }
  if (field && kinds?.has(field)) return formatFieldValue(value, kinds.get(field), field) || '—'
  return String(value)
}

/** What changed, in one line: old → new for a field, otherwise the gist. */
function changeOf(entry: StageAuditEntry, kinds?: ReadonlyMap<string, string>): string {
  const d = entry.detail ?? {}
  if (entry.kind === 'field_edit' || entry.kind === 'stage_change') {
    const field = String(d.field ?? '')
    return `${field.replace(/_/g, ' ')}: ${show(d.from, field, kinds)} → ${show(d.to, field, kinds)}`
  }
  if (entry.kind === 'note') return String(d.body ?? entry.summary)
  if (entry.kind === 'email_received') return String(d.subject ?? entry.summary)
  const bits = [d.name, d.filename, d.label, d.how, d.slot]
    .filter((b) => b !== undefined && b !== null && b !== '')
    .map(String)
  if (d.replaced && typeof d.replaced === 'object') {
    bits.push(`replaced ${Object.values(d.replaced as Record<string, unknown>).map((v) => show(v)).join(', ')}`)
  }
  if (d.to && typeof d.to === 'object' && 'status' in (d.to as object)) {
    bits.push(`→ ${String((d.to as { status?: unknown }).status)}`)
  }
  if (d.reason) bits.push(`“${String(d.reason)}”`)
  return bits.join(' · ') || '—'
}

function sourceOf(entry: StageAuditEntry): string {
  const d = entry.detail ?? {}
  if (d.source) return String(d.source)
  if (d.ingestion_id) return `intake record ${String(d.ingestion_id)}`
  if (entry.kind === 'note') return 'Deal note'
  return d.event ? String(d.event).replace(/_/g, ' ') : entry.kind.replace(/_/g, ' ')
}

function Stat({
  label,
  value,
  tone,
  title,
  note,
}: {
  label: string
  value: string
  tone?: string
  title?: string
  note?: string
}) {
  return (
    <div className="min-w-0 px-3" data-audit-stat={label} title={title}>
      <div className={clsx('truncate text-base font-semibold', tone ?? 'text-stone-100')}>{value}</div>
      <div className="truncate text-[9px] uppercase tracking-wider text-stone-600">{label}</div>
      {note ? <div className="truncate text-[9px] text-stone-500">{note}</div> : null}
    </div>
  )
}

export function AuditPage({
  loanId,
  lineage,
  catalogueFieldNames,
  variances,
  notes,
  catalogue,
  activity,
  loading,
  selectedLoanStage,
  selectedSegmentIndex,
  onSelectSegment,
  fieldKinds,
}: {
  loanId: string
  lineage: LoanFieldLineageField[]
  /** The deal catalogue's field names. The lineage also tracks a few deal
   *  record columns the catalogue does not define (deal status, kill reason,
   *  investor…); counting them in read '133 tracked fields' beside the
   *  127-field catalogue (LPR-26), so the tile counts the catalogue and names
   *  the others beside it. */
  catalogueFieldNames?: ReadonlySet<string>
  /** The engine's own count of stated figures its arithmetic does not
   *  reproduce. The tile read 0 while the engine reported 14, because it
   *  counted only lineage rows in a "conflicting" state (ECON-25, LPR-10). */
  variances?: number
  notes: Note[]
  /** The whole field catalogue, grouped and editable. Every value the deal can
   *  carry lives here — including the ones no panel has a place for. */
  catalogue?: ReactNode
  activity: LoanActivityEvent[]
  loading: boolean
  selectedLoanStage?: string | null
  selectedSegmentIndex: number | null
  onSelectSegment: (index: number | null) => void
  /** Each catalogue field's kind (currency, percent, number…), so the Trail
   *  prints a change the way the catalogue prints the value. */
  fieldKinds?: ReadonlyMap<string, string>
}) {
  const [view, setView] = useState<AuditView>('trail')

  // The Trail's source is the stage-audit chain: the package's arrival, the
  // deal's creation, every field change with its old and new value, party,
  // document and checklist events, and every note. The activity feed alone
  // held three machine events on a deal received in August (DJA-05).
  const audit = useQuery({
    queryKey: LOAN_QUERY_KEYS.stageAudit(loanId),
    queryFn: () => loansService.getStageAudit(loanId),
    enabled: Boolean(loanId),
  })
  const segments = useMemo(() => audit.data?.segments ?? [], [audit.data])
  const segmentIndex = useMemo(() => {
    if (selectedSegmentIndex != null) return selectedSegmentIndex
    if (!selectedLoanStage) return null
    let last: number | null = null
    segments.forEach((segment, i) => {
      if (segment.stage === selectedLoanStage) last = i
    })
    return last
  }, [selectedSegmentIndex, selectedLoanStage, segments])
  const entries: StageAuditEntry[] = useMemo(() => {
    const all =
      segmentIndex != null && segments[segmentIndex]
        ? segments[segmentIndex].events
        : audit.data
          ? stageAuditEntries(audit.data)
          : []
    return [...all].sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0))
  }, [audit.data, segments, segmentIndex])

  const variancesCount = variances ?? lineage.filter((field) => Boolean(field.variance)).length
  const outsideCatalogue = catalogueFieldNames
    ? lineage.filter((field) => !catalogueFieldNames.has(field.fieldName)).map((field) => field.fieldName)
    : []
  const firstEvent = audit.data?.first_event_at ?? null

  if (loading) {
    return (
      <div className="flex h-48 items-center justify-center gap-2 text-[12px] text-stone-500">
        <Loader2 size={14} className="animate-spin" /> Loading the audit record…
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      <div className="flex shrink-0 flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <h2 className="text-sm font-semibold tracking-wide text-stone-100">Audit</h2>
          <p className="mt-0.5 text-[11px] text-stone-500">
            Every event, value and note recorded on this deal
            {firstEvent ? ` since ${new Date(firstEvent).toLocaleDateString()}` : ''}.
          </p>
        </div>
        <div className="grid min-w-[420px] grid-cols-4 divide-x divide-white/[0.07] rounded-md border border-white/[0.07] bg-white/[0.025] py-2 max-md:min-w-0 max-md:w-full">
          <Stat label="Events" value={String(audit.data ? entries.length : activity.length)} />
          {catalogueFieldNames ? (
            <Stat
              label="Catalog fields"
              value={String(catalogueFieldNames.size)}
              note={outsideCatalogue.length ? `+${outsideCatalogue.length} outside the catalog` : undefined}
              title={
                outsideCatalogue.length
                  ? `Also tracked, outside the catalog: ${outsideCatalogue.join(', ')}`
                  : undefined
              }
            />
          ) : (
            <Stat label="Tracked fields" value={String(lineage.length)} />
          )}
          <Stat label="Notes" value={String(notes.length)} />
          <Stat
            label="Variances"
            value={String(variancesCount)}
            tone={variancesCount > 0 ? 'text-amber-300' : undefined}
            title="Stated figures the deal's own arithmetic does not reproduce — the engine's count. Shown for information; nothing is chosen between them."
          />
        </div>
      </div>

      <div className="flex shrink-0 gap-1 border-b border-white/[0.07]">
        {VIEWS.map((item) => (
          <button
            key={item.id}
            type="button"
            title={item.hint}
            onClick={() => setView(item.id)}
            className={clsx(
              'border-b px-3 py-2 text-[10px] uppercase tracking-[0.12em] transition',
              view === item.id
                ? 'border-amber-300 text-amber-300'
                : 'border-transparent text-stone-500 hover:text-stone-300',
            )}
          >
            {item.label}
          </button>
        ))}
        {view === 'trail' && segmentIndex != null && segments[segmentIndex] ? (
          <button
            type="button"
            onClick={() => onSelectSegment(null)}
            className="ml-auto self-center rounded border border-amber-400/25 px-2 py-0.5 text-[10px] text-amber-200"
          >
            Stage: {segments[segmentIndex].stage} — show every stage
          </button>
        ) : null}
      </div>

      <div className="min-h-0 flex-1">
      {view === 'trail' ? (
        audit.isLoading ? (
          <EmptyLine>Loading the trail…</EmptyLine>
        ) : (
          // Keyed: the All-fields catalogue is also an AuditTable at this
          // position, and without distinct keys React would carry this
          // view's actor filter over and hide every field.
          <AuditTable
            key="trail"
            columns={TRAIL_COLUMNS}
            sectionFilter="Everyone"
            stateFilter={false}
            items={entries.map((entry, i) => ({
              id: `${entry.at}-${i}`,
              label: entry.summary,
              value: changeOf(entry, fieldKinds),
              section: entry.actor,
              state: stamp(entry.at),
              source: sourceOf(entry),
              details: () => (
                <>
                  <dl className="grid grid-cols-2 gap-3 text-xs">
                    <dt>When</dt><dd>{stamp(entry.at)}</dd>
                    <dt>By</dt><dd>{entry.actor}</dd>
                    <dt>Change</dt><dd>{changeOf(entry, fieldKinds)}</dd>
                    <dt>Source</dt><dd>{sourceOf(entry)}</dd>
                  </dl>
                  <h4 className="text-xs text-amber-200">Recorded detail</h4>
                  <pre className="whitespace-pre-wrap break-words rounded border border-stone-800 p-3 text-xs">
                    {JSON.stringify(entry.detail, null, 2)}
                  </pre>
                </>
              ),
            }))}
          />
        )
      ) : view === 'fields' ? (
        catalogue ?? <EmptyLine>The field catalog is still loading.</EmptyLine>
      ) : notes.length ? (
        <AuditTable key="notes" items={notes.map(note => ({ id: note.id, label: note.author, value: note.body, section: 'Notes', state: stamp(note.createdAt), source: 'Deal note', details: () => <div className="whitespace-pre-wrap text-sm">{note.body}</div> }))}/>
      ) : <EmptyLine>No notes have been written on this deal.</EmptyLine>}
      </div>
    </div>
  )
}

export default AuditPage
